// src/pages/skills/SkillDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getSkillById,
  updateSkillStatus,
  deleteSkill,
} from "../../services/skillService";
import {
  getNotesBySkill,
  addNote,
  deleteNote,
} from "../../services/skillNoteService";

const statusColors = {
  Planned: "bg-gray-100 text-gray-700",
  "In Progress": "bg-yellow-100 text-yellow-800",
  Completed: "bg-green-100 text-green-700",
  "On Hold": "bg-red-100 text-red-700",
};

const SkillDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [skill, setSkill] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const skillData = await getSkillById(id);
      const noteData = await getNotesBySkill(id);
      setSkill(skillData);
      setNotes(noteData);
    }
    fetchData();
  }, [id]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    const result = await addNote({
      skillId: id,
      noteDate: new Date().toISOString().slice(0, 10),
      note: newNote,
    });

    if (result.success) {
      const updatedNotes = await getNotesBySkill(id);
      setNotes(updatedNotes);
      setNewNote("");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm("Delete this note?")) {
      await deleteNote(noteId);
      const updatedNotes = await getNotesBySkill(id);
      setNotes(updatedNotes);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setUpdatingStatus(true);
    const result = await updateSkillStatus(id, newStatus);
    if (result.success) {
      setSkill((prev) => ({ ...prev, status: newStatus }));
    }
    setUpdatingStatus(false);
  };

  /* ================= DELETE SKILL ================= */
  const handleDeleteSkill = async () => {
    if (!window.confirm("Are you sure you want to delete this skill?")) return;

    try {
      await deleteSkill(id);
      navigate("/skills");
    } catch (err) {
      alert("Failed to delete skill.");
    }
  };

  if (!skill) {
    return <div className="p-6 text-center text-gray-500">Loading skill…</div>;
  }

  return (
    <div className="p-0 md:p-4 max-w-4xl mx-auto pb-24">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{skill.name}</h1>
          <p className="text-sm text-gray-500">{skill.category}</p>
        </div>

        <div className="flex gap-4 items-center">
          <button
            onClick={handleDeleteSkill}
            className="text-sm text-red-600 hover:underline"
          >
            Delete
          </button>

          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-600 hover:underline"
          >
            Back
          </button>
        </div>
      </div>

      {/* SKILL INFO CARD */}
      <div className="bg-white rounded-xl shadow-sm border p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Level:</strong> {skill.level}
          </div>

          <div>
            <strong>Target Date:</strong>{" "}
            {skill.targetDate || "—"}
          </div>

          <div className="sm:col-span-2 flex items-center gap-2">
            <strong>Status:</strong>
            <select
              value={skill.status}
              onChange={handleStatusChange}
              disabled={updatingStatus}
              className={`px-3 py-1 rounded-full text-xs border ${
                statusColors[skill.status]
              }`}
            >
              <option value="Planned">Planned</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
            </select>
          </div>
        </div>
      </div>

      {/* NOTES SECTION */}
      <div className="bg-white rounded-xl shadow-sm border p-5">
        <h2 className="text-xl font-semibold mb-4">
          📒 Daily Progress Notes
        </h2>

        {notes.length === 0 ? (
          <p className="text-gray-500 text-sm">
            No notes yet. Start tracking your learning ✨
          </p>
        ) : (
          <ul className="space-y-3 mb-4">
            {notes.map((note) => (
              <li
                key={note.id}
                className="bg-yellow-50 border rounded-lg p-3 flex justify-between gap-4"
              >
                <div>
                  <p className="text-gray-800">{note.note}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(note.targetDate).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteNote(note.id)}
                  className="text-red-600 text-xs hover:underline"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* ADD NOTE */}
        <div className="mt-4">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={3}
            placeholder="What did you learn today?"
            className="w-full border rounded-lg p-2 mb-2"
          />
          <button
            onClick={handleAddNote}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            + Add Note
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillDetail;
